/**
 * UserTraining Integration
 * Bridges SDK user-training service to my-training store (no client.js import).
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { useMyTrainingStore } from '@/store/my-training.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

/** Map a backend user-training row to the my-training list shape. */
export function mapTrainingRow(item) {
    const id = item._id || item.id;
    const evaluationReview = item.evaluationReview || null;
    const reviewStatus = evaluationReview?.status;

    return {
        id,
        trainingId: item.training?._id || item.training?.id,
        trainingName: item.training?.title || item.training?.trainingName || '-',
        trainingProvider:
            item.training?.instructor?.name ||
            item.training?.externalInstructor ||
            item.training?.trainingProvider ||
            '-',
        trainingDuration: item.training?.duration || item.training?.trainingDuration || '0',
        startDate: item.training?.scheduledDate || item.startDate,
        endDate: item.training?.deadlineDate || item.training?.endDate || item.endDate,
        watchStatus: item.watchStatus || { watched: false },
        contentProgress: item.contentProgress || {
            completedModuleIds: [],
            completed: false,
        },
        requireContentBeforeEvaluation: item.training?.requireContentBeforeEvaluation !== false,
        evaluationStatus: {
            completed: item.evaluationStatus?.completed || false,
            submittedAt: item.evaluationStatus?.submittedAt,
            formId: item.evaluationStatus?.formId || item.evaluationResponses?.formId || null,
            formName: item.evaluationStatus?.formName || item.evaluationResponses?.formName || null,
            submittedFormIds:
                item.evaluationStatus?.submittedFormIds ||
                (item.evaluationStatus?.formId ? [item.evaluationStatus.formId] : []),
            answers:
                item.evaluationStatus?.answers ||
                item.evaluationResponses?.answers ||
                (Array.isArray(item.evaluationResponses) ? item.evaluationResponses : []),
        },
        hasSurveyEvaluation: (() => {
            const ids = item.training?.surveyFormTemplateIds;
            return Array.isArray(ids) && ids.length > 0;
        })(),
        evaluationReview,
        // Scored + approved/failed by evaluator → Completed tab
        isEvaluationReviewed: reviewStatus === 'approved' || reviewStatus === 'failed',
        passed: item.passed,
        bestScore: item.bestScore,
        progress: item.progress || 0,
        status: item.status,
        originalTraining: item.training,
        isArchived: item.isArchived || false,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
    };
}

export class UserTrainingIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    async fetchAll(params = {}) {
        try {
            const response = await this.service.getAll(params);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async getById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async create(data) {
        try {
            const response = await this.service.create(data);
            toast.success(
                i18next.t('integrationToasts:createdSuccessfully', {
                    defaultValue: 'Created successfully',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async update(id, data) {
        try {
            const response = await this.service.update(id, data);
            toast.success(
                i18next.t('integrationToasts:updatedSuccessfully', {
                    defaultValue: 'Updated successfully',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async delete(id) {
        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('integrationToasts:deletedSuccessfully', {
                    defaultValue: 'Deleted successfully',
                }),
            );
            return { success: true };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /**
     * Fill my-training store from searchParams / columnFilters / listTab.
     * List errors set store.error only (no toast).
     */
    async fetchMyTrainings() {
        const store = useMyTrainingStore.getState();
        store.setLoading(true);
        store.setError(null);

        try {
            const { searchTerm, page, pageSize } = store.searchParams;
            const columnFilters = store.columnFilters;
            const listTab = store.listTab;

            const response = await this.service.getMyTrainings({
                page,
                pageSize,
                search: searchTerm,
            });

            const rawData = Array.isArray(response.data)
                ? response.data
                : response.data?.data || [];
            // Drop rows whose Training parent is missing (orphan enrollments)
            const trainings = rawData
                .map(mapTrainingRow)
                .filter((row) => Boolean(row.trainingId || row.originalTraining));

            let filteredTrainings = trainings;

            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                filteredTrainings = filteredTrainings.filter(
                    (t) =>
                        t.trainingName.toLowerCase().includes(term) ||
                        t.trainingProvider.toLowerCase().includes(term),
                );
            }

            for (const [field, values] of Object.entries(columnFilters || {})) {
                if (!Array.isArray(values) || values.length === 0) continue;
                filteredTrainings = filteredTrainings.filter((training) =>
                    values.includes(training[field]),
                );
            }

            if (listTab === 'completed') {
                filteredTrainings = filteredTrainings.filter((t) => t.isEvaluationReviewed);
            } else {
                filteredTrainings = filteredTrainings.filter((t) => !t.isEvaluationReviewed);
            }

            const startIndex = (page - 1) * pageSize;
            const paginatedTrainings = filteredTrainings.slice(
                startIndex,
                startIndex + pageSize,
            );

            const uniqueValues = {
                trainingName: [
                    ...new Set(trainings.map((t) => t.trainingName).filter(Boolean)),
                ].sort(),
                trainingProvider: [
                    ...new Set(trainings.map((t) => t.trainingProvider).filter(Boolean)),
                ].sort(),
            };

            const totalPages = Math.ceil(filteredTrainings.length / pageSize) || 1;

            useMyTrainingStore.setState({
                myTrainings: paginatedTrainings,
                pagination: {
                    page,
                    currentPage: page,
                    pageSize,
                    itemsPerPage: pageSize,
                    total: filteredTrainings.length,
                    totalItems: filteredTrainings.length,
                    totalPages,
                    hasNextPage: page < totalPages,
                    hasPreviousPage: page > 1,
                },
                uniqueValues,
                loading: false,
            });

            return { success: true, data: paginatedTrainings };
        } catch (error) {
            const message = this._getErrorMessage(error);
            useMyTrainingStore.setState({ error: message, loading: false });
            return { success: false, error: message };
        }
    }

    /** Thin SDK pass-through; toast.error on fail. */
    async getMyTrainings(params = {}) {
        try {
            const response = await this.service.getMyTrainings(params);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async markVideoAsWatched(userTrainingId, videoId) {
        try {
            const response = await this.service.markVideoAsWatched(userTrainingId, {
                videoId,
            });
            toast.success(
                i18next.t('integrationToasts:videoMarkedAsWatched', {
                    defaultValue: 'Video marked as watched',
                }),
            );
            await this.fetchMyTrainings();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async markContentProgress(userTrainingId, moduleId = null, opts = {}) {
        try {
            const response = await this.service.markContentProgress(userTrainingId, {
                ...(moduleId ? { moduleId } : {}),
            });
            if (!opts.silent && response.data?.allDone) {
                toast.success(
                    i18next.t('iso27001MyTraining:messages.contentCompleted', {
                        defaultValue: 'Training content completed',
                    }),
                );
            }
            if (response.data?.allDone) {
                await this.fetchMyTrainings();
            }
            return response.data;
        } catch (error) {
            useMyTrainingStore.setState({ error: this._getErrorMessage(error) });
            toast.error(this._getErrorMessage(error));
            throw error;
        }
    }

    async submitEvaluationAnswers(userTrainingId, answers) {
        const store = useMyTrainingStore.getState();
        store.setLoading(true);
        store.setError(null);
        try {
            const response = await this.service.submitEvaluation(userTrainingId, answers);
            toast.success(
                i18next.t('integrationToasts:evaluationSubmitted', {
                    defaultValue: 'Evaluation submitted',
                }),
            );
            await this.fetchMyTrainings();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            store.setLoading(false);
            toast.error(message);
            throw error;
        }
    }

    /** Alias for submitEvaluationAnswers. */
    async submitEvaluation(userTrainingId, answers) {
        return this.submitEvaluationAnswers(userTrainingId, answers);
    }

    async getSurveyTasks(id) {
        try {
            const response = await this.service.getSurveyTasks(id);
            const rows = response?.data?.data || response?.data || [];
            return { success: true, data: Array.isArray(rows) ? rows : [] };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error), data: [] };
        }
    }

    async getEvaluationBoard(trainingId) {
        try {
            const response = await this.service.getEvaluationBoard(trainingId);
            return { success: true, data: response.data || null };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error), data: null };
        }
    }

    async saveEvaluationScores(id, payload, opts = {}) {
        try {
            const response = await this.service.saveEvaluationScores(id, payload);
            if (!opts.silent) {
                toast.success(
                    i18next.t('iso27001Training:messages.scoresSaved', {
                        defaultValue: 'Scores saved',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async getEvaluationResult(id) {
        try {
            const response = await this.service.getEvaluationResult(id);
            return { success: true, data: response.data || null };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error), data: null };
        }
    }

    async approveEvaluation(id, payload = {}) {
        try {
            const response = await this.service.approveEvaluation(id, payload);
            const passed = response.passed ?? response.data?.passed;
            toast.success(
                passed
                    ? i18next.t('iso27001Training:messages.evaluationApprovedPass', {
                          defaultValue: 'Approved — learner passed',
                      })
                    : i18next.t('iso27001Training:messages.evaluationApprovedFail', {
                          defaultValue: 'Recorded as failed (below passing score)',
                      }),
            );
            return { success: true, data: response.data, passed };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /**
     * @param {string} id
     * @param {{ mode: 'survey_retake'|'full_restart', notes?: string }} payload
     */
    async remediateEvaluation(id, payload) {
        try {
            const response = await this.service.remediateEvaluation(id, payload);
            const isRestart = payload?.mode === 'full_restart';
            toast.success(
                isRestart
                    ? i18next.t('iso27001Training:messages.remediateRestartSuccess', {
                          defaultValue: 'Training restarted — learner must rewatch videos',
                      })
                    : i18next.t('iso27001Training:messages.remediateRetakeSuccess', {
                          defaultValue: 'Surveys reassigned for retake',
                      }),
            );
            return { success: true, data: response.data, mode: response.mode };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async bulkUpdateResults(results) {
        try {
            const response = await this.service.bulkUpdateResults(results);
            toast.success(
                i18next.t('integrationToasts:resultsUpdatedSuccessfully', {
                    defaultValue: 'Results updated successfully',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /** Silent progress write; then refresh my-training list. */
    async updateProgress(userTrainingId, progress) {
        try {
            const response = await this.service.updateProgress(userTrainingId, { progress });
            await this.fetchMyTrainings();
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async archiveTraining(userTrainingId) {
        const training = useMyTrainingStore
            .getState()
            .myTrainings.find((t) => t.id === userTrainingId);
        if (training && training.status !== 'completed' && !training.watchStatus?.watched) {
            toast.error(
                i18next.t('iso27001MyTraining:messages.archiveCompletedOnly', {
                    defaultValue: 'Only completed trainings can be archived.',
                }),
            );
            return {
                success: false,
                error: 'Only completed trainings can be archived',
            };
        }

        try {
            const response = await this.service.archive(userTrainingId);
            toast.success(
                i18next.t('integrationToasts:trainingArchived', {
                    defaultValue: 'Training archived',
                }),
            );
            await this.fetchMyTrainings();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /** Alias for archiveTraining. */
    async archive(userTrainingId) {
        return this.archiveTraining(userTrainingId);
    }

    async unarchiveTraining(userTrainingId) {
        try {
            const response = await this.service.unarchive(userTrainingId);
            toast.success(
                i18next.t('integrationToasts:trainingActivated', {
                    defaultValue: 'Training activated',
                }),
            );
            await this.fetchMyTrainings();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /** Alias for unarchiveTraining. */
    async unarchive(userTrainingId) {
        return this.unarchiveTraining(userTrainingId);
    }
}
